import React, { useEffect, useMemo, useState } from 'react';
import { Eye, PencilSimple, Plus, Tag, Trash } from '@phosphor-icons/react';
import PageHeader from '../../../shared/components/ui/PageHeader';
import GenericFilters from '../../../shared/components/ui/GenericFilters';
import GenericTables from '../../../shared/components/ui/GenericTables';
import ModalCreationEditing from '../../../shared/components/ui/ModalCreationEditing';
import ActionMenu from '../../../shared/components/ui/ActionMenu';
import { categoryService } from '../../../services/tasks/categoryService.js';
import { useGuidedTourStep } from '../../../shared/hooks/useGuidedTour.js';

const CategoryManagement = ({ userRole }) => {
	const canWrite = userRole === 'admin' || userRole === 'superadmin';

	const [categories, setCategories] = useState([]);
	const [loading, setLoading] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [notification, setNotification] = useState({
		show: false,
		message: '',
		type: 'success',
		origin: '',
	});
	const [filters, setFilters] = useState({
		displayName: '',
		isActive: 'active',
	});

	const [modalOpen, setModalOpen] = useState(false);
	const [modalMode, setModalMode] = useState('create');
	const [selectedCategory, setSelectedCategory] = useState(null);

	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [totalItems, setTotalItems] = useState(0);
	const [totalPages, setTotalPages] = useState(1);

	useEffect(() => {
		fetchCategories();
	}, [filters.displayName, filters.isActive, currentPage, pageSize]);

	useEffect(() => {
		if (!notification.show) return undefined;

		const timeoutId = setTimeout(() => {
			setNotification({ show: false, message: '', type: 'success', origin: '' });
		}, 5000);

		return () => clearTimeout(timeoutId);
	}, [notification.show]);

	const fetchCategories = async () => {
		try {
			setLoading(true);
			setErrorMessage('');
			const params = {
				...filters,
				pageSize,
				pageCurrent: currentPage - 1,
			};
			const response = await categoryService.getCategories(params);
			const data = response.categories;
			setCategories(data);

			if (response.pagination) {
				const responseTotalItems = Number(response.pagination.totalItems) || 0;
				const responsePageSize = Number(response.pagination.pageSize) || pageSize;

				setTotalItems(responseTotalItems);
				setTotalPages(responseTotalItems > 0 ? Math.ceil(responseTotalItems / responsePageSize) : 1);
			} else {
				setTotalItems(data.length);
				setTotalPages(1);
			}
		} catch (error) {
			console.error('Error loading categories:', error);
			setErrorMessage('No se pudieron cargar las categorías.');
		} finally {
			setLoading(false);
		}
	};

	const handlePageChange = (newPage) => {
		setCurrentPage(newPage);
	};

	const handlePageSizeChange = (newPageSize) => {
		setPageSize(newPageSize);
		setCurrentPage(1);
	};

	const openCreateModal = () => {
		setSelectedCategory(null);
		setModalMode('create');
		setModalOpen(true);
		setNotification({ show: false, message: '', type: 'success', origin: '' });
	};

	const openViewModal = (category) => {
		setSelectedCategory(category);
		setModalMode('view');
		setModalOpen(true);
		setNotification({ show: false, message: '', type: 'success', origin: '' });
	};

	const openEditModal = (category) => {
		setSelectedCategory(category);
		setModalMode('edit');
		setModalOpen(true);
		setNotification({ show: false, message: '', type: 'success', origin: '' });
	};

	const openDeleteModal = (category) => {
		setSelectedCategory(category);
		setModalMode('delete');
		setModalOpen(true);
		setNotification({ show: false, message: '', type: 'success', origin: '' });
	};

	const closeModal = () => {
		if (isSubmitting) return;
		setModalOpen(false);
		setSelectedCategory(null);
		setNotification((prev) =>
			prev.origin === 'categories-modal' ? { show: false, message: '', type: 'success', origin: '' } : prev
		);
	};

	// Recorrido guiado (taskTypesTour.js): el paso con modal 'category-create' abre el alta y lo cierra al salir.
	const [tourStep, setTourStep] = useState(null);
	useGuidedTourStep((step) => {
		if (step?.modal === 'category-create' && tourStep?.modal !== 'category-create') openCreateModal();
		if (step?.modal !== 'category-create' && tourStep?.modal === 'category-create') closeModal();
		setTourStep(step);
	});

	const modalInitialValues = useMemo(
		() => ({
			name: selectedCategory?.name || '',
			displayName: selectedCategory?.displayName || '',
			estimatedDuration: selectedCategory?.estimatedDuration || '',
			description: selectedCategory?.description || '',
		}),
		[selectedCategory]
	);

	const showNotification = (message, type = 'error', origin = 'categories-modal') => {
		setNotification({
			show: true,
			message,
			type,
			origin,
		});
	};

	const handleSubmitCategory = async (values) => {
		const displayName = values.displayName?.trim() || '';
		const payload = {
			displayName,
			description: values.description?.trim() || '',
			estimatedDuration: values.estimatedDuration ? Number(values.estimatedDuration) : undefined,
		};

		if (modalMode === 'create') {
			payload.name = values.name?.trim() || '';
		}

		setIsSubmitting(true);
		setErrorMessage('');
		setNotification({ show: false, message: '', type: 'success', origin: '' });

		try {
			if (modalMode === 'create') {
				await categoryService.createCategory(payload);
				showNotification('Categoría creada correctamente.', 'success', 'categories-page');
			}

			if (modalMode === 'edit') {
				await categoryService.updateCategory(selectedCategory.name, payload);
				showNotification('Categoría actualizada correctamente.', 'success', 'categories-page');
			}

			setModalOpen(false);
			setSelectedCategory(null);
			await fetchCategories();
		} catch (error) {
			console.error('Error saving category:', error);
			showNotification(error.message || 'No se pudo guardar la categoría.', 'error', 'categories-modal');
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDeactivateCategory = async () => {
		if (!selectedCategory?.name) {
			showNotification('No se pudo identificar la categoría a desactivar.', 'error', 'categories-modal');
			return;
		}

		setIsSubmitting(true);
		setErrorMessage('');
		setNotification({ show: false, message: '', type: 'success', origin: '' });

		try {
			await categoryService.deactivateCategory(selectedCategory.name);
			showNotification('Categoría desactivada correctamente.', 'success', 'categories-page');
			setModalOpen(false);
			setSelectedCategory(null);
			await fetchCategories();
		} catch (error) {
			console.error('Error deactivating category:', error);
			showNotification(error.message || 'No se pudo desactivar la categoría.', 'error', 'categories-modal');
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleRestoreCategory = async (category) => {
		if (!category?.name) {
			showNotification('No se pudo identificar la categoría a restaurar.', 'error', 'categories-page');
			return;
		}

		setIsSubmitting(true);
		setErrorMessage('');

		try {
			await categoryService.restoreCategory(category.name);
			showNotification('Categoría restaurada correctamente.', 'success', 'categories-page');
			await fetchCategories();
		} catch (error) {
			console.error('Error restoring category:', error);
			showNotification(error.message || 'No se pudo restaurar la categoría.', 'error', 'categories-page');
		} finally {
			setIsSubmitting(false);
		}
	};

	const filterColumns = [
		{
			type: 'text',
			key: 'displayName',
			label: 'Nombre',
		},
		{
			type: 'select',
			key: 'isActive',
			label: 'Estado',
			options: [
				{ id: 'active', name: 'Activas' },
				{ id: 'inactive', name: 'Inactivas' },
			],
		},
	];

	const modalFields = [
		{
			name: 'displayName',
			tour: 'category-form-name',
			label: 'Nombre',
			type: 'text',
			required: true,
			maxLength: 100,
			placeholder: 'Ej. Instalación',
			disabled: () => modalMode === 'view',
		},
		{
			name: 'estimatedDuration',
			tour: 'category-form-duration',
			label: 'Duración estimada (minutos)',
			type: 'number',
			required: true,
			min: 1,
			placeholder: 'Ej. 90',
			disabled: () => modalMode === 'view',
		},
		{
			name: 'description',
			tour: 'category-form-description',
			label: 'Descripción',
			type: 'textarea',
			required: false,
			maxLength: 500,
			placeholder: 'Descripción opcional',
			colSpan: 2,
			disabled: () => modalMode === 'view',
		},
	];

	const isInactiveFilter = filters.isActive === 'inactive';

	const tableColumns = [
		{
			key: 'displayName',
			label: 'Nombre',
			align: 'center',
			render: (row) => row.displayName || '-',
		},
		{
			key: 'estimatedDuration',
			label: 'Duración estimada',
			align: 'center',
			render: (row) => (row.estimatedDuration ? `${row.estimatedDuration} min` : '-'),
		},
		{
			key: 'description',
			label: 'Descripción',
			align: 'center',
			render: (row) => {
				if (!row.description) return '-';
				return (
					<div className="max-w-xs truncate mx-auto" title={row.description}>
						{row.description}
					</div>
				);
			}
		},
		{
			key: 'isActive',
			label: 'Estado',
			align: 'center',
			isChip: true,
			render: (row) => (
				<span
					className={`text-xs font-medium px-2 py-1 rounded-full border ${
						row.isActive
							? 'bg-green-900/30 text-green-400 border-green-700/40'
							: 'bg-surface-2/40 text-content-muted border-line/40'
					}`}
				>
					{row.isActive ? 'Activa' : 'Inactiva'}
				</span>
			),
		},
		{
			key: 'actions',
			label: 'Acciones',
			align: 'center',
			isAction: true,
			render: (row) => {
				const options = [{ label: 'Ver', icon: <Eye size={16} />, onClick: () => openViewModal(row) }];

				if (canWrite) {
					if (isInactiveFilter) {
						options.push({ label: 'Restaurar', icon: <Plus size={16} />, onClick: () => handleRestoreCategory(row) });
					} else {
						options.push({ label: 'Editar', icon: <PencilSimple size={16} />, onClick: () => openEditModal(row) });
						options.push({ label: 'Desactivar', icon: <Trash size={16} />, onClick: () => openDeleteModal(row) });
					}
				}

				return <span data-tour="category-row-actions" className="inline-flex"><ActionMenu options={options} /></span>;
			},
		},
	];

	return (
		<div className="space-y-6">
			<div data-tour="categories-header">
			<PageHeader
				title="Categorías"
				icon={<Tag size={24} weight="duotone" />}
				subtitle="Administra las categorías de tarea de tu empresa"
				actions={
					canWrite ? (
						<button
							type="button"
							onClick={openCreateModal}
							className="bg-amber hover:bg-amber-hover text-on-amber px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-semibold transition-colors"
						>
							<Plus size={18} />
							Crear categoría
						</button>
					) : undefined
				}
			/>
			</div>

			{errorMessage && (
				<div className="rounded-lg border border-red-700 bg-red-900/20 p-3 text-sm text-red-300">{errorMessage}</div>
			)}

			{notification.show && notification.origin === 'categories-page' && (
				<div
					className={`rounded-lg border p-3 text-sm ${
						notification.type === 'success'
							? 'border-green-700 bg-green-900/20 text-green-300'
							: 'border-red-700 bg-red-900/20 text-red-300'
					}`}
				>
					{notification.message}
				</div>
			)}

			<div data-tour="categories-filters">
			<GenericFilters
				columns={filterColumns}
				filters={filters}
				onFilterChange={(nextFilters) => {
					setFilters(nextFilters);
					setCurrentPage(1);
				}}
				onClearFilters={() => {
					setFilters({ displayName: '', isActive: 'active' });
					setCurrentPage(1);
				}}
				loading={loading}
			/>
			</div>

			<div data-tour="categories-list" className="relative">
				<GenericTables
					columns={tableColumns}
					data={categories}
					emptyMessage="No hay categorías disponibles"
					showFooter
					pagination={{
						currentPage,
						totalPages,
						pageSize,
						totalItems,
						pageSizeOptions: [5, 10, 20, 50],
						onPageChange: handlePageChange,
						onPageSizeChange: handlePageSizeChange,
					}}
				/>

				{loading && (
					<div className="absolute inset-0 flex items-center justify-center bg-bg/60 rounded-xl">
						<div className="flex items-center gap-3 rounded-lg border border-line bg-surface/90 px-4 py-3 text-sm text-content shadow-lg">
							<span className="h-5 w-5 animate-spin rounded-full border-2 border-amber border-t-transparent" />
							Cargando categorías...
						</div>
					</div>
				)}
			</div>

			<ModalCreationEditing
				isOpen={modalOpen}
				mode={modalMode === 'create' ? 'create' : modalMode === 'delete' ? 'delete' : 'edit'}
				icon={<Tag size={22} weight="duotone" />}
				title={
					modalMode === 'delete'
						? 'Desactivar categoría'
						: modalMode === 'view'
							? 'Ver categoría'
							: modalMode === 'edit'
								? 'Editar categoría'
								: 'Crear categoría'
				}
				submitText={modalMode === 'edit' ? 'Guardar cambios' : 'Crear categoría'}
				columns={2}
				fields={modalMode === 'delete' ? [] : modalFields}
				initialValues={modalInitialValues}
				notification={notification.origin === 'categories-modal' ? notification : null}
				onSubmit={modalMode === 'delete' ? undefined : handleSubmitCategory}
				onClose={closeModal}
				isSubmitting={isSubmitting}
				actions={
					modalMode === 'delete' ? (
						<div className="rounded-lg border border-red-700 bg-red-900/20 p-4 text-red-200">
							¿Estás seguro de que quieres desactivar la categoría: <span className="font-semibold">{selectedCategory?.displayName || '-'}</span>?
						</div>
					) : undefined
				}
				footer={
					modalMode === 'delete' ? (
						<div className="flex justify-end gap-4 pt-6 border-t border-line mt-6">
							<button
								type="button"
								onClick={closeModal}
								disabled={isSubmitting}
								className="px-6 py-3 bg-surface-2 text-content rounded-lg hover:bg-surface-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
							>
								Cancelar
							</button>
							<button
								type="button"
								onClick={handleDeactivateCategory}
								disabled={isSubmitting}
								className="px-6 py-3 bg-red-600 text-content rounded-lg hover:bg-red-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
							>
								{isSubmitting ? 'Desactivando...' : 'Confirmar'}
							</button>
						</div>
					) : modalMode === 'view' ? (
						<div className="flex justify-end gap-4 pt-6 border-t border-line mt-6">
							<button
								type="button"
								onClick={closeModal}
								className="px-6 py-3 bg-surface-2 text-content rounded-lg hover:bg-surface-2 transition-colors"
							>
								Cerrar
							</button>
						</div>
					) : undefined
				}
			/>
		</div>
	);
};

export default CategoryManagement;
